import React, { useEffect, useRef, useState } from 'react'

import {
  Box,
  Button,
  Flex,
  HStack,
  Spacer,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { caseworkSchema, initialCaseworkValues } from '../../constants'
import { createCaseworkInstance } from '../../redux/features/caseworkSlice'
import {
  AttachmentsPage,
  CaseworkFormStepper,
  CaseworkInfoPage,
  ConstituentSelectPage,
  IntakeInfoPage,
  ReviewPage,
  SelectDistrictPage,
} from '../casework'
import {
  formatFormValues,
  formatInitialValues,
} from '../casework/casework/caseworkForm/utils'
import { validateForm as validateNewConstituentValues } from '../directory/constituents/createOrEditConstituent/utils'

const CreateCasework = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const user = useSelector(state => state.auth.user)
  const pageContentRef = useRef(null)
  const [formPage, setFormPage] = useState(0)
  const [missingFields, setMissingFields] = useState(false)
  const [constituentMode, setConstituentMode] = useState('select') // select from directory or create new
  const [lastVisitedPage, setLastVisitedPage] = useState(0)

  useEffect(() => {
    if (pageContentRef.current) pageContentRef.current.scroll({ top: 0 })
  }, [formPage, pageContentRef])

  useEffect(() => {
    if (formPage > lastVisitedPage) setLastVisitedPage(formPage)
  }, [formPage, lastVisitedPage, setLastVisitedPage])

  document.title = 'Create Casework'
  const pages = [
    {
      title: 'Constituent',
      description: 'Select existing constituent or create new one',
      component: ConstituentSelectPage,
      componentProps: {
        mode: constituentMode,
        setMode: mode => {
          setMissingFields(false)
          setConstituentMode(mode)
        },
      },
      validatePage: (setErrors, values) => {
        let valid = true
        let errors = {}
        if (!values.constituent && constituentMode === 'select') {
          errors['constituent'] = 'Required field'
          valid = false
        } else if (constituentMode === 'create') {
          const newConstituentErrors = validateNewConstituentValues(
            values.newConstituent
          )

          if (Object.values(newConstituentErrors).length) {
            errors['newConstituent'] = { ...newConstituentErrors }
            valid = false
          }
        }

        setErrors(errors)
        return valid
      },
    },
    {
      title: 'Casework Details',
      description: 'Provide casework information',
      component: CaseworkInfoPage,
      componentProps: {
        constituentMode: constituentMode,
      },
      validatePage: (setErrors, values) => {
        let valid = true

        let errors = {}
        if (values.status === 'Closed' && values.topics.length === 0) {
          errors['topics'] = 'Topic required to close casework'
          valid = false
        }
        if (!values.details) {
          errors['details'] = 'Required field'
          valid = false
        }

        if (!valid) setErrors(errors)

        return valid
      },
    },
    {
      title: 'Intake Information',
      description: 'Provide intake information',
      component: IntakeInfoPage,
      componentProps: {},
      fields: [
        'contact_methods',
        'reference_numbers',
        'referral_staff',
        'comments',
        'opened_at',
        'closed_at',
      ],
      validatePage: (setErrors, values) => {
        const { opened_at, closed_at } = values
        let valid = true
        if (!opened_at || !closed_at) return valid
        if (opened_at > closed_at) {
          valid = false
          setErrors({
            opened_at: 'Open date cannot be later than close date',
            closed_at: 'Close date cannot be earlier than open date',
          })
        }
        return valid
      },
    },
    {
      title: 'Attachments',
      description: 'Upload any relevant attachments',
      component: AttachmentsPage,
      componentProps: {},
      fields: ['attachments'],
      validatePage: () => true,
    },
    {
      title: 'Review',
      description: 'Review all details before submitting',
      component: ReviewPage,
      componentProps: {
        creatingConstituent: constituentMode === 'create',
      },
      fields: [],
      validatePage: () => true,
    },
  ]

  if (user?.permissions?.includes('casework.edit_district')) {
    pages.unshift({
      title: 'Select District',
      description: 'Select district',
      component: SelectDistrictPage,
      componentProps: {},
      validatePage: () => true,
    })
  }

  const CurrentPage = pages[formPage].component

  // Establish initial values
  const initialValues = formatInitialValues(initialCaseworkValues)
  if (location.state?.constituent)
    initialValues.constituent = location.state?.constituent
  if (user.role === 'Intern') initialValues.assigned_staff.push(user)
  if (!initialValues.intake_staff) initialValues.intake_staff = user
  initialValues.district = user.district
  initialValues.assigned_staff = []

  const formikProps = {
    initialValues: initialValues,
    validationSchema: caseworkSchema,
    validateOnChange: false,
    validate: values => {
      if (constituentMode === 'create') {
        const newConstituentErrors = validateNewConstituentValues(
          values.newConstituent
        )
        if (Object.values(newConstituentErrors).length) {
          const errors = { newConstituentErrors }
          return errors
        }
      }
    },
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true)

      const callbackSuccess = case_num => {
        navigate(`/casework/${case_num}`)
        setSubmitting(false)
      }

      const callbackFailure = () => {
        setSubmitting(false)
      }

      dispatch(
        createCaseworkInstance({
          values: formatFormValues(values, constituentMode),
          callbackSuccess,
          callbackFailure,
        })
      )
    },
  }

  return (
    <>
      <Box m='auto' maxW='5xl' mt={5}>
        <Formik {...formikProps}>
          {({
            values,
            errors,
            isSubmitting,
            handleSubmit,
            setFieldValue,
            setErrors,
          }) => {
            const handleChangePage = page => {
              if (page < formPage) setFormPage(page)
              if (!pages[formPage].validatePage(setErrors, values)) {
                setMissingFields(true)
                return
              }
              setMissingFields(false)
              setErrors({})
              if (page < lastVisitedPage || formPage + 1 === page)
                setFormPage(page)
            }

            const handlePrevPage = () => {
              handleChangePage(formPage === 0 ? formPage : formPage - 1)
            }

            const handleNextPage = () => {
              handleChangePage(
                formPage === pages.length - 1 ? formPage : formPage + 1
              )
            }

            return (
              <>
                <CaseworkFormStepper
                  steps={pages}
                  activeStep={formPage}
                  onChangeStep={handleChangePage}
                  onNext={handleNextPage}
                  onPrevious={handlePrevPage}
                />
                <Form>
                  <Box
                    m='auto'
                    maxW='5xl'
                    my={2}
                    h='68vh'
                    overflowY='auto'
                    px={2}
                    ref={pageContentRef}
                  >
                    <CurrentPage {...pages[formPage].componentProps} />
                  </Box>

                  <Stack>
                    {formPage === pages.length - 1 &&
                      JSON.stringify(errors) !== '{}' && (
                        <Box align='end'>
                          <Text color='red'>Required fields are missing!</Text>
                        </Box>
                      )}
                    <Flex align='center' w='100%'>
                      <Button
                        visibility={formPage === 0 ? 'hidden' : 'visible'}
                        onClick={handlePrevPage}
                        variant='outline'
                      >
                        Previous Step
                      </Button>
                      <Spacer />
                      {formPage === pages.length - 1 ? (
                        <>
                          <HStack>
                            <Text>Close Casework</Text>
                            <Switch
                              id='status'
                              defaultChecked={
                                !!values.closed_at || values.status === 'Closed'
                              }
                              isDisabled={!!values.closed_at}
                              onChange={e => {
                                if (e.target.checked)
                                  setFieldValue('status', 'Closed')
                                else setFieldValue('status', 'In Progress')
                              }}
                            />
                          </HStack>
                          <Button
                            onClick={handleSubmit}
                            h={50}
                            isLoading={isSubmitting}
                            ml={3}
                          >
                            Create Casework
                          </Button>
                        </>
                      ) : (
                        <>
                          {missingFields && (
                            <Text color='red' mr={3}>
                              Required fields were not filled in.
                            </Text>
                          )}
                          <Button onClick={handleNextPage} variant='outline'>
                            Next Step
                          </Button>
                        </>
                      )}
                    </Flex>
                  </Stack>
                </Form>
              </>
            )
          }}
        </Formik>
      </Box>
    </>
  )
}

export default CreateCasework
